/**
 * Test-only mount for `ConflictDialog`.
 *
 * The production conflict surface is wired into the SaveProvider /
 * vault-outbound paths (E.4 status surface) but those need a real 412
 * to fire. Phase C drafts a vitest smoke covering render + click;
 * this bridge lets the Phase J `conflict-dialog.spec.ts` exercise the
 * dialog mounted at the desktop root with full overlay/click behavior.
 *
 * Listens for `repancy:test-force-conflict-dialog` CustomEvent dispatched
 * by `window.__repancyTestForceConflictDialog` (defined in
 * `src/lib/test-hooks/index.ts`). Gated on `?test=1` checked inside the
 * handler so the dialog can never appear in production traffic.
 *
 * @see packages/repancy/e2e/conflict-dialog.spec.ts
 */

import { useEffect, useState, type ReactElement } from 'react';

import { ConflictDialog } from '../../components/sharepoint/ConflictDialog';
import type { ConflictResolution } from '../graph/saveBack';

interface MountState {
  documentTitle: string;
  remoteAuthor?: string;
  onResolve?: (r: ConflictResolution) => void;
  onCancel?: () => void;
}

export function ConflictDialogTestMount(): ReactElement | null {
  const [state, setState] = useState<MountState | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const listener = (event: Event) => {
      try {
        if (new URLSearchParams(window.location.search).get('test') !== '1') {
          return;
        }
      } catch {
        return;
      }
      const ce = event as CustomEvent<MountState>;
      setState({
        documentTitle: ce.detail.documentTitle,
        remoteAuthor: ce.detail.remoteAuthor,
        onResolve: ce.detail.onResolve,
        onCancel: ce.detail.onCancel,
      });
    };
    window.addEventListener('repancy:test-force-conflict-dialog', listener);
    return () =>
      window.removeEventListener('repancy:test-force-conflict-dialog', listener);
  }, []);

  if (!state) return null;
  return (
    <ConflictDialog
      open={true}
      documentTitle={state.documentTitle}
      remoteAuthor={state.remoteAuthor}
      onResolve={(r) => {
        state.onResolve?.(r);
        setState(null);
      }}
      onCancel={() => {
        state.onCancel?.();
        setState(null);
      }}
    />
  );
}
